import type { ForwardedRef, KeyboardEvent, ReactNode } from 'react';
import { forwardRef, memo, useCallback } from 'react';
import { isEqual } from 'lodash-es';

import { Loader } from '../../Elements/Loader/index.tsx';
import { Tooltip } from '../../Feedback/Tooltip/index.tsx';
import {
  noop,
  valueAsSingleValue,
  valueAsMultiValue,
  onChangeAsSingle,
  onChangeAsMulti,
  getIsClickable,
  isValueSelected,
} from './helpers.ts';
import type { ChipItemProps } from './styled.ts';
import { ChipItem, ChipsWrapper } from './styled.ts';
import type { ItemOption, ChipsOnChangeValue, PropsValue } from './types.ts';

export interface ChipsProps<TValue, TMulti extends boolean> {
  options: ItemOption<NonNullable<TValue>>[];
  selectedValue: PropsValue<TValue>;
  onChange: (value: ChipsOnChangeValue<TValue, TMulti>) => void;
  multi?: TMulti;
  inline?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  spacer?: boolean;
  formatOptionLabel?: (option: ItemOption<NonNullable<TValue>>) => string;
  dataTestId?: string;
  nowrap?: boolean;
  getOptionTooltip?: (value: TValue) => string | undefined;
}

type ChipsComponentType = <TValue, TMulti extends boolean = false>(
  props: ChipsProps<TValue, TMulti> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReactNode;

export const Chips = memo(
  forwardRef(function Chips<TValue, TMulti extends boolean = false>(props: ChipsProps<TValue, TMulti>, ref: ForwardedRef<HTMLDivElement>) {
    const {
      options,
      selectedValue,
      onChange,
      multi,
      inline,
      disabled: disabledProp,
      spacer,
      isClearable,
      isLoading,
      formatOptionLabel,
      dataTestId,
      nowrap,
      getOptionTooltip,
    } = props;

    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const updateSelectedValue = useCallback(
      (value: TValue) => {
        if (multi) {
          const val = valueAsMultiValue(selectedValue);
          if (val.some((item) => isEqual(item, value))) {
            if (isClearable || val.length > 1) {
              onChangeAsMulti(onChange)(val.filter((item) => !isEqual(item, value)));
            }
          } else {
            onChangeAsMulti(onChange)([...val, value]);
          }
        } else {
          const val = valueAsSingleValue(selectedValue);
          onChangeAsSingle(onChange)(isClearable && isEqual(val, value) ? null : value);
        }
      },
      [isClearable, multi, onChange, selectedValue],
    );

    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const handleChange = useCallback(
      (value: TValue) => () => {
        updateSelectedValue(value);
      },
      [updateSelectedValue],
    );

    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const handleKeyPress = useCallback(
      (value: TValue) => (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          updateSelectedValue(value);
        }
      },
      [updateSelectedValue],
    );

    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const getItemProps = useCallback(
      ({ value, disabled: disabledItem }: ItemOption<TValue>): ChipItemProps => {
        const disabled = disabledProp || disabledItem;

        return {
          $multi: multi,
          checked: isValueSelected(selectedValue, value, multi),
          disabled,
          $isClickable: getIsClickable(selectedValue, value, multi, isClearable),
          onClick: disabled ? noop : handleChange(value),
          onKeyDown: disabled ? noop : handleKeyPress(value),
          tabIndex: 0,
          $labelTooltip: getOptionTooltip?.(value),
        };
      },
      [disabledProp, handleChange, handleKeyPress, isClearable, multi, selectedValue, getOptionTooltip],
    );

    return (
      <ChipsWrapper
        $inline={inline}
        $spacer={spacer}
        $isLoading={isLoading}
        tabIndex={0}
        ref={ref}
        data-testid={dataTestId}
        $nowrap={nowrap}>
        {isLoading ? (
          <Loader dataTestId='chips-loader' />
        ) : (
          options.map((option) => {
            const { $labelTooltip, ...itemProps } = getItemProps(option);
            return (
              <Tooltip placement='top' key={option.label} tooltip={$labelTooltip}>
                <ChipItem {...itemProps} data-testid={`${option.label.toLowerCase()}-${itemProps.checked}-chip`}>
                  {formatOptionLabel ? formatOptionLabel(option) : option.label}
                </ChipItem>
              </Tooltip>
            );
          })
        )}
      </ChipsWrapper>
    );
  }),
) as ChipsComponentType;
